import { SHStack, SHToggleButtonOptions } from "@components/design-systems";
import { AnalysisSummaryDTO } from "@models/platform-analysis/entities/steps/summary";
import { FeatureAnalysisScoreMode } from "@models/platform-analysis/enums/feature/featureAnalysisScoreMode";
import { TableViewMode } from "@models/platform-analysis/enums/tableViewMode";
import { FeatureAnalysisDisplayScoresOptions } from "@pages/platform-analysis-v2/_id/constants";
import { memo, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useMount } from "react-use";
import { AnalysisSummaryStaticValueConfigs } from "../../../configs";
import { FeatureAnalysisTableViewModeOptions } from "../../../constants";

interface SummaryFeatureAnalysisTableToolbarProps {
  disabled?: boolean;
  disableDetailView?: boolean;
  onViewModeChange?: (viewMode: TableViewMode) => void;
  onShowAnalysisScoreChange?: (showAnalysisScore: boolean) => void;
}
export const SummaryFeatureAnalysisTableToolbar = memo(
  ({
    disabled,
    disableDetailView,
    onViewModeChange,
    onShowAnalysisScoreChange,
  }: SummaryFeatureAnalysisTableToolbarProps) => {
    const { control, getValues } = useFormContext<AnalysisSummaryDTO>();

    const [localViewMode, setLocalViewMode] = useState<TableViewMode>();

    const handleViewModeChange = (value: TableViewMode) => {
      setLocalViewMode(value);
      onViewModeChange?.(value);
    };

    const shouldShowAnalysisScoreButtons = useMemo(() => {
      return localViewMode === TableViewMode.Detail;
    }, [localViewMode]);

    useMount(() => {
      const initViewMode = getValues("featureAnalysisTableViewMode");
      handleViewModeChange(initViewMode);
    });

    return (
      <SHStack direction={"row"} alignItems={"center"} gap={2}>
        {shouldShowAnalysisScoreButtons && (
          <Controller
            control={control}
            name="showAnalysisScore"
            render={({ field: { onChange, value } }) => (
              <SHToggleButtonOptions
                disabled={disabled}
                options={FeatureAnalysisDisplayScoresOptions}
                value={
                  value
                    ? FeatureAnalysisScoreMode.Show
                    : FeatureAnalysisScoreMode.Hide
                }
                onChange={(_, value: FeatureAnalysisScoreMode) => {
                  const isShowScore = value === FeatureAnalysisScoreMode.Show;
                  onChange(isShowScore);
                  onShowAnalysisScoreChange?.(isShowScore);
                }}
              />
            )}
          />
        )}

        <SHStack width={"max-content"}>
          <Controller
            control={control}
            name="featureAnalysisTableViewMode"
            render={({ field: { value, onChange } }) => (
              <SHToggleButtonOptions
                width={"fit-content"}
                disabled={disabled}
                options={FeatureAnalysisTableViewModeOptions.map((option) => ({
                  ...option,
                  disabled:
                    option.value === TableViewMode.Detail && disableDetailView,
                  tooltip:
                    option.value === TableViewMode.Detail && disableDetailView
                      ? `Select less than ${AnalysisSummaryStaticValueConfigs.MaximumFeaturesInDetailView} features for detail view`
                      : undefined,
                }))}
                value={value}
                onChange={(_, value) => {
                  if (!value) return;
                  onChange(value);
                  handleViewModeChange(value);
                }}
              />
            )}
          />
        </SHStack>
      </SHStack>
    );
  },
);
